import { useState, useCallback } from 'react'
import { useSessaoCapturaCtx } from '../../contexts/SessaoCapturaContext'
import { useProjetosAtivos } from '../../hooks/useProjectos'
import { compressImage } from '@/lib/compressImage'
import SessaoCapturaHeader   from './SessaoCapturaHeader'
import SessaoCapturaTimeline from './SessaoCapturaTimeline'
import SessaoCapturaToolbar  from './SessaoCapturaToolbar'
import SessaoCapturaResumo   from './SessaoCapturaResumo'
import CapturaDrawCanvas     from './CapturaDrawCanvas'
import CameraInline          from './CameraInline'
import FotoAnnotate          from './FotoAnnotate'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'

interface TextOverlayProps {
  onSave: (text: string) => void
  onCancel: () => void
}

interface DocDrawerProps {
  open: boolean
  projetoId: number | null | undefined
  onClose: () => void
}

interface ArquivoRevisao {
  id: number
  nome_arquivo: string
  storage_path: string | null
  tipo_arquivo: string | null
  signedUrl?: string | null
}

interface Revisao {
  id: number
  numero_rev: number
  arquivos_revisao: ArquivoRevisao[]
}

interface EtapaDoc {
  id: number
  tipo: string
  numero_ep: number
  revisoes: Revisao[]
}

// Overlay de texto inline
function TextOverlay({ onSave, onCancel }: TextOverlayProps) {
  const [text, setText] = useState<string>('')
  return (
    <div
      className="absolute bottom-0 left-0 right-0 flex flex-col gap-2 p-4"
      style={{ background: 'rgba(12,12,12,0.95)', borderTop: '1px solid rgba(255,255,255,0.10)', zIndex: 5 }}
    >
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="Digite sua anotação..."
        rows={4}
        className="w-full p-3 rounded-xl text-[14px] text-[#f0f0f0] placeholder:text-[#444] resize-none outline-none"
        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}
      />
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-2 rounded-xl text-[12px] text-[#888] border-none bg-transparent cursor-pointer">Cancelar</button>
        <button
          onClick={() => text.trim() && onSave(text.trim())}
          disabled={!text.trim()}
          className="flex-1 py-2 rounded-xl text-[12px] font-semibold border-none cursor-pointer disabled:opacity-40 rounded-full"
          style={{ background: 'var(--color-hero)', color: '#111' }}
        >
          Salvar
        </button>
      </div>
    </div>
  )
}

// Drawer de documentos do projeto
function DocDrawer({ open, projetoId, onClose }: DocDrawerProps) {
  const [docs,    setDocs]    = useState<EtapaDoc[] | null>(null)
  const [viewer,  setViewer]  = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const loadDocs = useCallback(async () => {
    if (!projetoId || docs) return
    setLoading(true)
    const { data } = await supabase
      .from('etapas_projeto')
      .select('id, tipo, numero_ep, revisoes(id, numero_rev, arquivos_revisao(id, nome_arquivo, storage_path, tipo_arquivo))')
      .eq('projeto_id', projetoId)
      .order('numero_ep')
    if (data) {
      const allPaths = data.flatMap(e => e.revisoes ?? []).flatMap(r => r.arquivos_revisao ?? [])
        .filter(a => a.storage_path).map(a => a.storage_path)
      let urlMap: Record<string, string> = {}
      if (allPaths.length) {
        const { data: signed } = await supabase.storage.from('arquivos-etapas').createSignedUrls(allPaths, 3600)
        ;(signed ?? []).forEach(({ path, signedUrl }) => { if (signedUrl) urlMap[path] = signedUrl })
      }
      setDocs(data.map(e => ({ ...e, revisoes: (e.revisoes ?? []).map(r => ({
        ...r,
        arquivos_revisao: (r.arquivos_revisao ?? []).map(a => ({ ...a, signedUrl: urlMap[a.storage_path] ?? null })),
      }))})))
    }
    setLoading(false)
  }, [projetoId, docs])

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="bottom" className="h-[70vh]" onOpenAutoFocus={() => loadDocs()}>
        <SheetHeader><SheetTitle>Documentos do projeto</SheetTitle></SheetHeader>
        {loading && <p className="text-[12px] text-[#555] p-4">Carregando...</p>}
        {docs && (
          <div className="overflow-y-auto h-full pb-8">
            {docs.map(etapa => (etapa.revisoes ?? []).flatMap(rev =>
              (rev.arquivos_revisao ?? []).filter(a => a.signedUrl).map(arq => (
                <button key={arq.id} onClick={() => setViewer(arq.signedUrl)}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-left border-none bg-transparent cursor-pointer hover:bg-white/[0.04] transition-colors rounded-lg">
                  <span className="material-symbols-outlined text-[#555] text-[16px]">image</span>
                  <span className="text-[12px] text-[#ccc] truncate flex-1">{arq.nome_arquivo}</span>
                </button>
              ))
            ))}
          </div>
        )}
        {viewer && (
          <div className="fixed inset-0 z-[320] flex items-center justify-center bg-black/90" onClick={() => setViewer(null)}>
            <img src={viewer} alt="Documento" className="max-w-full max-h-full object-contain" style={{ touchAction: 'pinch-zoom' }} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default function SessaoCaptura() {
  const {
    sessao, items, seconds, expanded, minimize, encerrar, descartar,
    addNota, addNotaBackground, updateNota, addLegendaVoz, deleteNota,
    changeProjeto, recuperarSessao,
  } = useSessaoCapturaCtx()

  const { projetos } = useProjetosAtivos(50)

  const [showResumo,  setShowResumo]  = useState<boolean>(false)
  const [showCanvas,  setShowCanvas]  = useState<boolean>(false)
  const [showText,    setShowText]    = useState<boolean>(false)
  const [showDocs,    setShowDocs]    = useState<boolean>(false)
  const [showDiscard, setShowDiscard] = useState<boolean>(false)
  const [showCamera,  setShowCamera]  = useState<boolean>(false)
  const [annotateNota, setAnnotateNota] = useState<Record<string, unknown> | null>(null)

  if (!sessao || !expanded) return null

  const projetoAtivo = projetos.find(p => p.id === sessao.projeto_id)

  const handleEncerrar = async (titulo: string | null) => {
    setShowResumo(false)
    await encerrar(titulo)
  }

  const handleDescartar = () => { setShowResumo(false); setShowDiscard(true) }
  const handleConfirmDiscard = async () => { setShowDiscard(false); await descartar() }

  const handleSaveText = async (text: string) => {
    setShowText(false)
    await addNota({ tipo: 'TEXTO', conteudo: text })
  }

  const handleSaveCanvas = async (blob: Blob) => {
    setShowCanvas(false)
    await addNota({ tipo: 'DESENHO', file: blob })
  }

  const handleCameraCapture = async (blob: Blob) => {
    const file = new File([blob], `foto-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' })
    const { file: compressed } = await compressImage(file, { maxDimension: 1200, quality: 0.8, minSizeToCompress: 0 })
    addNotaBackground({ tipo: 'FOTO', file: compressed })
  }

  const handleChangeProjeto = async (id: number | null) => {
    await changeProjeto(id)
    toast.success(id ? 'Projeto vinculado' : 'Projeto removido')
  }

  const handleRefresh = async () => {
    await recuperarSessao()
  }

  return (
    <div className="fixed inset-0 z-[300] flex flex-col" style={{ background: 'var(--color-bg)' }}>
      <SessaoCapturaHeader
        sessao={sessao}
        seconds={seconds}
        projetoNome={projetoAtivo?.nome ?? null}
        projetoCodigo={projetoAtivo?.codigo_projeto ?? null}
        projetos={projetos}
        onChangeProjeto={handleChangeProjeto}
        onMinimize={minimize}
        onOpenDocs={() => setShowDocs(true)}
      />

      <div className="relative flex-1 flex flex-col min-h-0">
        <SessaoCapturaTimeline
          items={items}
          updateNota={updateNota}
          addLegendaVoz={addLegendaVoz}
          deleteNota={deleteNota}
          onOpenAnnotate={setAnnotateNota}
          onRefresh={handleRefresh}
        />
        {showText && (
          <TextOverlay onSave={handleSaveText} onCancel={() => setShowText(false)} />
        )}
      </div>

      <SessaoCapturaToolbar
        onOpenText={()    => setShowText(true)}
        onOpenCanvas={()  => setShowCanvas(true)}
        onOpenResumo={()  => setShowResumo(true)}
        onOpenCamera={()  => setShowCamera(true)}
      />

      {showCamera && (
        <CameraInline
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
      {showCanvas && <CapturaDrawCanvas onConfirm={handleSaveCanvas} onCancel={() => setShowCanvas(false)} />}
      {annotateNota && (
        <FotoAnnotate
          imageUrl={annotateNota.signedUrl}
          onConfirm={async (blob) => {
            const nota = await addNota({ tipo: 'FOTO', file: blob })
            if (nota && !String(annotateNota.id).startsWith('temp_')) {
              await deleteNota(annotateNota)
            }
            setAnnotateNota(null)
          }}
          onCancel={() => setAnnotateNota(null)}
        />
      )}

      <SessaoCapturaResumo
        open={showResumo}
        items={items}
        seconds={seconds}
        onConfirm={handleEncerrar}
        onCancel={() => setShowResumo(false)}
        onDescartar={handleDescartar}
      />

      <AlertDialog open={showDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar sessão?</AlertDialogTitle>
            <AlertDialogDescription>
              Todas as capturas serão apagadas permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscard(false)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              style={{ background: 'var(--color-vermelho)' }}
            >
              Descartar tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocDrawer open={showDocs} projetoId={sessao.projeto_id} onClose={() => setShowDocs(false)} />
    </div>
  )
}
